import { Link } from 'react-router-dom';
import { buttonClasses } from '@/components/ui/button-styles';
import { EARNING_IDEAS, SKILL_PILLS } from '@/content/site-content';
import { EARNINGS_IMAGE } from '@/content/images';
import { ArrowRightIcon, BoltIcon } from '@/components/ui/Icons';

/**
 * The loud, teen-facing pitch. Everything above the fold of this section is
 * deliberately informal — the highlighter mark, the sticky note, the
 * handwritten aside — and the three earning ideas below it land the point in
 * plain numbers.
 */
export function EarningsSection() {
  return (
    <section id="earn" className="scroll-mt-28">
      <div className="bg-sunrise relative overflow-hidden">
        <div className="container-page grid items-center gap-10 py-16 lg:grid-cols-[1.05fr_1fr] lg:py-20">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-sun-100 px-3.5 py-1.5 text-xs font-extrabold tracking-[0.06em] text-sun-700">
              <BoltIcon className="size-3.5" />
              NEW · FOR REAL, THIS TIME
            </span>

            <h2 className="mt-6 text-4xl leading-[1.1] font-extrabold sm:text-5xl">
              Stop asking for money.
              <br />
              <span className="mark-sun">Start making it.</span>
            </h2>

            <p className="mt-6 max-w-md text-base leading-relaxed text-ink-soft">
              Dog walking, tutoring, selling stuff you make — whatever you&rsquo;re into, someone
              nearby will pay you for it.{' '}
              <span className="font-bold text-brand-600">Yes, really.</span>
            </p>

            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/register" className={buttonClasses('secondary', 'md', 'rounded-full')}>
                I&rsquo;m in
                <ArrowRightIcon className="size-4" />
              </Link>
              <Link to="/jobs" className={buttonClasses('outline', 'md', 'rounded-full')}>
                Show me the money 💸
              </Link>
            </div>

            <figure className="mt-10 flex max-w-sm items-center gap-3">
              <span
                aria-hidden="true"
                className="grid size-11 shrink-0 place-items-center rounded-full bg-white text-lg shadow-soft ring-1 ring-line"
              >
                🐕
              </span>
              <div className="rounded-2xl rounded-bl-md bg-safe-50 px-4 py-3">
                <blockquote className="text-sm font-semibold text-ink">
                  &ldquo;made $85 my first week walking dogs&rdquo;
                </blockquote>
                <figcaption className="mt-0.5 text-xs text-ink-soft">Maya, 16</figcaption>
              </div>
            </figure>
          </div>

          <div className="relative mx-auto w-full max-w-md lg:max-w-none">
            {/* The sticky note. */}
            <p
              aria-hidden="true"
              className="absolute top-4 -left-1 z-10 w-32 -rotate-6 rounded-sm bg-sun-400 px-3 py-2.5 text-center text-xs font-bold text-ink shadow-soft sm:left-2"
            >
              Your Skills
              <span className="block text-base">=</span>
              Real Cash
            </p>

            <img
              src={EARNINGS_IMAGE.src}
              alt={EARNINGS_IMAGE.alt}
              width={EARNINGS_IMAGE.width}
              height={EARNINGS_IMAGE.height}
              loading="lazy"
              decoding="async"
              className="aspect-4/3 w-full rounded-[2rem] object-cover shadow-lift"
            />

            {/* The skill labels flying off the photo. */}
            <ul
              aria-hidden="true"
              className="absolute top-6 -right-2 flex w-40 flex-col items-end gap-2 sm:-right-4 sm:w-44"
            >
              {SKILL_PILLS.map((pill, index) => (
                <li
                  key={pill.label}
                  style={{ marginRight: `${index % 2 === 0 ? 0 : 0.75}rem` }}
                  className={`flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-bold shadow-soft ${pill.tint}`}
                >
                  <span>{pill.emoji}</span>
                  {pill.label}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* The three earning ideas, on the pale band from the design. */}
      <div className="bg-indigo-50/60">
        <ul className="container-page grid gap-5 py-10 md:grid-cols-3">
          {EARNING_IDEAS.map((idea) => (
            <li key={idea.name} className="rounded-2xl bg-white p-5 shadow-soft">
              <div className="flex items-center gap-3">
                <span
                  aria-hidden="true"
                  className={`grid size-9 shrink-0 place-items-center rounded-xl text-base ${idea.chip}`}
                >
                  {idea.emoji}
                </span>
                <h3 className="text-base font-bold text-ink">{idea.name}</h3>
              </div>
              <p className="mt-3 text-sm text-ink-soft">{idea.description}</p>
              <p
                className={`mt-4 inline-block rounded-full px-3 py-1 text-xs font-bold ${idea.pill}`}
              >
                {idea.rate}
              </p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
